import React, { useState } from "react";
import styled from "styled-components";
import { Menu, X } from "lucide-react";
import { Button } from "../../../general";
import { SidebarEntry } from "../../bars";

export interface SidebarLink {
  label: string;
  icon: React.ReactNode;
  path: string;
  notifications?: number;
}

interface SidebarSplitProps {
  links: SidebarLink[];
  compact?: boolean;
  sticky?: boolean;
  logo?: React.ReactNode;
  hoverExpand?: boolean;
  activePath?: string;
  onSelect?: (path: string) => void;
  toggleOpenIcon?: React.ReactNode;
  toggleCloseIcon?: React.ReactNode;
}

const SidebarContainer = styled.aside<{ expanded: boolean; mobileOpen: boolean; sticky?: boolean }>`
  width: ${({ expanded }) => (expanded ? "260px" : "72px")};
  height: 100vh;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  box-shadow: ${({ theme }) => theme.elevations.low};
  display: flex;
  flex-direction: column;
  position: ${({ sticky }) => (sticky ? "sticky" : "fixed")};
  top: 0;
  left: ${({ mobileOpen }) => (mobileOpen ? "0" : "-260px")};
  z-index: 1100;
  transition: all 0.3s ease;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    left: 0;
  }
`;

const SidebarContent = styled.div<{ expanded: boolean }>`
  width: ${({ expanded }) => (expanded ? "260px" : "72px")};
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.md};
  padding-inline: ${({ theme, expanded }) => (expanded ? theme.spacing.md : "0")};
  transition: width 0.3s ease;
`;

const SidebarHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ expanded }) => (expanded ? "flex-end" : "center")};
  padding-bottom: ${({ theme }) => theme.spacing.md};
`;

const SidebarLinksWrapper = styled.div<{ expanded: boolean }>`
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: ${({ expanded }) => (expanded ? "flex-start" : "center")};
  gap: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.md};
`;

const MobileBackdrop = styled.div<{ mobileOpen: boolean }>`
  display: ${({ mobileOpen }) => (mobileOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1090;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileToggle = styled(Button)`
  position: fixed;
  bottom: 16px;
  left: 16px;
  z-index: 1200;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const SidebarSplit: React.FC<SidebarSplitProps> = ({
  links,
  compact = false,
  sticky = true,
  logo,
  hoverExpand = false,
  activePath,
  onSelect,
  toggleOpenIcon,
  toggleCloseIcon,
}) => {
  const [isCompact, setIsCompact] = useState(compact);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isExpanded = !isCompact || (hoverExpand && isHovered);

  return (
    <>
      <MobileBackdrop mobileOpen={mobileOpen} onClick={() => setMobileOpen(false)} />
      <MobileToggle variant="primary" onClick={() => setMobileOpen(!mobileOpen)}>
        {mobileOpen ? toggleCloseIcon || <X size={20} /> : toggleOpenIcon || <Menu size={20} />}
      </MobileToggle>

      <SidebarContainer
        expanded={isExpanded}
        mobileOpen={mobileOpen}
        sticky={sticky}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <SidebarContent expanded={isExpanded}>
          <SidebarHeader expanded={isExpanded}>
            {logo && isExpanded && <div>{logo}</div>}
            {!hoverExpand && (
              <Button variant="ghost" size="compact" onClick={() => setIsCompact(!isCompact)}>
                {isExpanded ? toggleCloseIcon || <X size={20} /> :  toggleOpenIcon || <Menu size={20} />}
              </Button>
            )}
          </SidebarHeader>

          <SidebarLinksWrapper expanded={isExpanded}>
            {links.map((link, index) => (
              <SidebarEntry
                key={index}
                label={link.label}
                icon={link.icon}
                path={link.path}
                count={link.notifications}
                compact={!isExpanded}
                active={link.path === activePath}
                onClick={() => onSelect?.(link.path)}
              />
            ))}
          </SidebarLinksWrapper>
        </SidebarContent>
      </SidebarContainer>
    </>
  );
};